import { AxiosError } from "axios";
import { 
    QuestionCategory, 
    CreateQuestionCategoryRequest, 
    UpdateQuestionCategoryRequest,
    QuestionCategoryFilters,
    EducationProgram,
    CreateEducationProgramRequest,
    UpdateEducationProgramRequest,
    EducationProgramFilters,
    QuestionModule,
    CreateQuestionModuleRequest,
    UpdateQuestionModuleRequest,
    QuestionUnit,
    CreateQuestionUnitRequest,
    UpdateQuestionUnitRequest,
    BloomsTaxonomyLevel,
    CreateBloomsTaxonomyLevelRequest,
    UpdateBloomsTaxonomyLevelRequest
} from "@/types/examination.types";
import axios from "@/axios.config";
import { ApiResponse } from "@/types/fetch.type";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BulkImportRequestDTO, BulkImportResultDTO } from "@/types/examination.types";

// Legacy Education Programs (kept for backward compatibility - maps to education-programs endpoint)
export const useGetQuestionCategories = (filters?: QuestionCategoryFilters) => {
    return useQuery<ApiResponse<EducationProgram[]>, AxiosError>({
        queryKey: ["education-programs-legacy", filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (filters) {
                Object.entries(filters).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        params.append(key, value.toString());
                    }
                });
            }
            const response = await axios.get<ApiResponse<EducationProgram[]>>(`/education-programs?${params}`);
            return response.data;
        },
    });
};

export const useGetQuestionCategoriesByCouncil = (councilId?: string) => {
    return useQuery<ApiResponse<EducationProgram[]>, AxiosError>({
        queryKey: ["education-programs-legacy", "council", councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<EducationProgram[]>>(`/education-programs/council/${councilId}`);
            return response.data;
        },
        enabled: !!councilId,
    });
};

export const useGetQuestionCategoryById = (id: string, enabled = true) => {
    return useQuery<ApiResponse<EducationProgram>, AxiosError>({
        queryKey: ["education-programs-legacy", id],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<EducationProgram>>(`/education-programs/${id}`);
            return response.data;
        },
        enabled: enabled && !!id,
    });
};

export const useCreateQuestionCategory = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<EducationProgram>, AxiosError<{ message: string }>, CreateEducationProgramRequest>({
        mutationFn: async (data) => {
            const response = await axios.post<ApiResponse<EducationProgram>>(`/education-programs`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["education-programs-legacy"] });
            queryClient.invalidateQueries({ queryKey: ["education-programs"] });
        },
    });
};

export const useUpdateQuestionCategory = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<EducationProgram>, AxiosError<{ message: string }>, UpdateEducationProgramRequest>({
        mutationFn: async (data) => {
            const response = await axios.put<ApiResponse<EducationProgram>>(`/education-programs/${data.id}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["education-programs-legacy"] });
            queryClient.invalidateQueries({ queryKey: ["education-programs"] });
        },
    });
};

export const useDeleteQuestionCategory = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<EducationProgram>, AxiosError<{ message: string }>, string>({
        mutationFn: async (id) => {
            const response = await axios.delete<ApiResponse<EducationProgram>>(`/education-programs/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["education-programs-legacy"] });
            queryClient.invalidateQueries({ queryKey: ["education-programs"] });
        },
    });
};

// Education Programs (New API endpoints - primary interface)
export const useGetEducationPrograms = (filters?: EducationProgramFilters) => {
    return useQuery<ApiResponse<EducationProgram[]>, AxiosError>({
        queryKey: ["education-programs", filters],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (filters) {
                Object.entries(filters).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        params.append(key, value.toString());
                    }
                });
            }
            const response = await axios.get<ApiResponse<EducationProgram[]>>(`/education-programs?${params}`);
            return response.data;
        },
    });
};

export const useGetEducationProgramsByCouncil = (councilId?: string) => {
    return useQuery<ApiResponse<EducationProgram[]>, AxiosError>({
        queryKey: ["education-programs", "council", councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<EducationProgram[]>>(`/education-programs/council/${councilId}`);
            return response.data;
        },
        enabled: !!councilId,
    });
};

export const useGetEducationProgramById = (id: string, enabled = true) => {
    return useQuery<ApiResponse<EducationProgram>, AxiosError>({
        queryKey: ["education-programs", id],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<EducationProgram>>(`/education-programs/${id}`);
            return response.data;
        },
        enabled: enabled && !!id,
    });
};

export const useGetEducationProgramByCode = (code: string, enabled = true) => {
    return useQuery<ApiResponse<EducationProgram>, AxiosError>({
        queryKey: ["education-programs", "code", code],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<EducationProgram>>(`/education-programs/code/${code}`);
            return response.data;
        },
        enabled: enabled && !!code,
    });
};

export const useCreateEducationProgram = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<EducationProgram>, AxiosError<{ message: string }>, CreateEducationProgramRequest>({
        mutationFn: async (data) => {
            const response = await axios.post<ApiResponse<EducationProgram>>(`/education-programs`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["education-programs"] });
            queryClient.invalidateQueries({ queryKey: ["education-programs-legacy"] }); // Also invalidate legacy cache
        },
    });
};

export const useUpdateEducationProgram = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<EducationProgram>, AxiosError<{ message: string }>, UpdateEducationProgramRequest>({
        mutationFn: async (data) => {
            const response = await axios.put<ApiResponse<EducationProgram>>(`/education-programs/${data.id}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["education-programs"] });
            queryClient.invalidateQueries({ queryKey: ["education-programs-legacy"] }); // Also invalidate legacy cache
        },
    });
};

export const useDeleteEducationProgram = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<EducationProgram>, AxiosError<{ message: string }>, string>({
        mutationFn: async (id) => {
            const response = await axios.delete<ApiResponse<EducationProgram>>(`/education-programs/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["education-programs"] });
            queryClient.invalidateQueries({ queryKey: ["education-programs-legacy"] }); // Also invalidate legacy cache
        },
    });
};

export const useToggleEducationProgramStatus = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<EducationProgram>, AxiosError<{ message: string }>, string>({
        mutationFn: async (id) => {
            const response = await axios.put<ApiResponse<EducationProgram>>(`/education-programs/${id}/toggle-status`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["education-programs"] });
            queryClient.invalidateQueries({ queryKey: ["education-programs-legacy"] }); // Also invalidate legacy cache
        },
    });
};

// Question Modules (unchanged - still use categoryId for backward compatibility)
export const useGetQuestionModules = (councilId?: string) => {
    return useQuery<ApiResponse<QuestionModule[]>, AxiosError>({
        queryKey: ["question-modules", "council", councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<QuestionModule[]>>(`/question-modules/council/${councilId}`);
            return response.data;
        },
        enabled: !!councilId,
    });
};


export const useGetQuestionModuleById = (id: string, enabled = true) => {
    return useQuery<ApiResponse<QuestionModule>, AxiosError>({
        queryKey: ["question-modules", id],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<QuestionModule>>(`/question-modules/${id}`);
            return response.data;
        },
        enabled: enabled && !!id,
    });
};

export const useCreateQuestionModule = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<QuestionModule>, AxiosError<{ message: string }>, CreateQuestionModuleRequest>({
        mutationFn: async (data) => {
            const response = await axios.post<ApiResponse<QuestionModule>>(`/question-modules`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["question-modules"] });
        },
    });
};

export const useUpdateQuestionModule = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<QuestionModule>, AxiosError<{ message: string }>, UpdateQuestionModuleRequest>({
        mutationFn: async (data) => {
            const response = await axios.put<ApiResponse<QuestionModule>>(`/question-modules/${data.id}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["question-modules"] });
        },
    });
};

export const useDeleteQuestionModule = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<QuestionModule>, AxiosError<{ message: string }>, string>({
        mutationFn: async (id) => {
            const response = await axios.delete<ApiResponse<QuestionModule>>(`/question-modules/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["question-modules"] });
        },
    });
};

// Question Units (unchanged - still use moduleId for backward compatibility)
export const useGetQuestionUnits = (councilId?: string) => {
    return useQuery<ApiResponse<QuestionUnit[]>, AxiosError>({
        queryKey: ["question-units", "council", councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<QuestionUnit[]>>(`/question-units/council/${councilId}`);
            return response.data;
        },
        enabled: !!councilId,
    });
};

export const useGetQuestionUnitsByModule = (moduleId: string) => {
    return useQuery<ApiResponse<QuestionUnit[]>, AxiosError>({
        queryKey: ["question-units", "module", moduleId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<QuestionUnit[]>>(`/question-units/module/${moduleId}`);
            return response.data;
        },
        enabled: !!moduleId,
    });
};

export const useGetQuestionUnitById = (id: string, enabled = true) => {
    return useQuery<ApiResponse<QuestionUnit>, AxiosError>({
        queryKey: ["question-units", id],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<QuestionUnit>>(`/question-units/${id}`);
            return response.data;
        },
        enabled: enabled && !!id,
    });
};

export const useCreateQuestionUnit = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<QuestionUnit>, AxiosError<{ message: string }>, CreateQuestionUnitRequest>({
        mutationFn: async (data) => {
            const response = await axios.post<ApiResponse<QuestionUnit>>(`/question-units`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["question-units"] });
        },
    });
};

export const useUpdateQuestionUnit = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<QuestionUnit>, AxiosError<{ message: string }>, UpdateQuestionUnitRequest>({
        mutationFn: async (data) => {
            const response = await axios.put<ApiResponse<QuestionUnit>>(`/question-units/${data.id}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["question-units"] });
        },
    });
};

export const useDeleteQuestionUnit = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<QuestionUnit>, AxiosError<{ message: string }>, string>({
        mutationFn: async (id) => {
            const response = await axios.delete<ApiResponse<QuestionUnit>>(`/question-units/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["question-units"] });
        },
    });
};

// Bloom's Taxonomy Levels (unchanged)
export const useGetBloomsTaxonomyLevels = (councilId?: string) => {
    return useQuery<ApiResponse<BloomsTaxonomyLevel[]>, AxiosError>({
        queryKey: ["blooms-taxonomy-levels", councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<BloomsTaxonomyLevel[]>>(`/blooms-taxonomy-levels`, { params: { councilId } });
            return response.data;
        },
        enabled: !!councilId,
    });
};

export const useGetBloomsTaxonomyLevelById = (id: string, enabled = true) => {
    return useQuery<ApiResponse<BloomsTaxonomyLevel>, AxiosError>({
        queryKey: ["blooms-taxonomy-levels", id],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<BloomsTaxonomyLevel>>(`/blooms-taxonomy-levels/${id}`);
            return response.data;
        },
        enabled: enabled && !!id,
    });
};

export const useCreateBloomsTaxonomyLevel = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<BloomsTaxonomyLevel>, AxiosError<{ message: string }>, CreateBloomsTaxonomyLevelRequest>({
        mutationFn: async (data) => {
            const response = await axios.post<ApiResponse<BloomsTaxonomyLevel>>(`/blooms-taxonomy-levels`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["blooms-taxonomy-levels"] });
        },
    });
};

export const useUpdateBloomsTaxonomyLevel = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<BloomsTaxonomyLevel>, AxiosError<{ message: string }>, UpdateBloomsTaxonomyLevelRequest>({
        mutationFn: async (data) => {
            const response = await axios.put<ApiResponse<BloomsTaxonomyLevel>>(`/blooms-taxonomy-levels/${data.id}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["blooms-taxonomy-levels"] });
        },
    });
};

export const useDeleteBloomsTaxonomyLevel = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<BloomsTaxonomyLevel>, AxiosError<{ message: string }>, string>({
        mutationFn: async (id) => {
            const response = await axios.delete<ApiResponse<BloomsTaxonomyLevel>>(`/blooms-taxonomy-levels/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["blooms-taxonomy-levels"] });
        },
    });
};

// Bulk Import Education Programs, Modules, Units
export const useBulkImportEducationData = (councilId?: string) => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<BulkImportResultDTO>, AxiosError<{ message: string }>, BulkImportRequestDTO>({
        mutationFn: async (payload) => {
            const url = `/education-programs/council/${councilId}/bulk-import`
            const response = await axios.post<ApiResponse<BulkImportResultDTO>>(url, payload);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate related caches
            queryClient.invalidateQueries({ queryKey: ["education-programs"] });
            queryClient.invalidateQueries({ queryKey: ["question-modules"] });
            queryClient.invalidateQueries({ queryKey: ["question-units"] });
        }
    });
};
